import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchCurrentCard, submitCardResponse, recordExit, CardData } from '../../services/trainingService';
import MarkdownRenderer from './MarkdownRenderer';
import '../../styles/training.css';

const OPTION_KEYS = ['A', 'B', 'C', 'D'];

export default function TrainingWeekPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [card, setCard] = useState<CardData | null>(null);
  const [totalCards, setTotalCards] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [employeeName, setEmployeeName] = useState('');
  const brandName = sessionStorage.getItem('training_brand') || 'FinaConnect';
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Answer state
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [mcqFeedback, setMcqFeedback] = useState<'correct' | 'incorrect' | null>(null);

  // Timing
  const cardShownAt = useRef<number>(Date.now());
  const [minTimeLeft, setMinTimeLeft] = useState(0);

  // Animation
  const [animating, setAnimating] = useState(false);
  const [slideDir, setSlideDir] = useState<'in' | 'out'>('in');
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const loadCard = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError('');
      const data = await fetchCurrentCard(token);
      if (data.completed) {
        navigate('/training/complete', { replace: true });
        return;
      }
      setCard(data.currentCard);
      setTotalCards(data.totalCards);
      setCurrentIndex(data.currentCardIndex);
      setEmployeeName(data.employeeName);
      setSelectedOption(null);
      setTextAnswer('');
      setMcqFeedback(null);
      cardShownAt.current = Date.now();
      setMinTimeLeft(data.currentCard.minTimeSeconds);
    } catch (err: any) {
      setError(err.message || 'Failed to load. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    const stored = sessionStorage.getItem('training_token');
    if (!stored || stored !== token) {
      navigate('/training', { replace: true });
      return;
    }
    loadCard();
  }, [token, navigate, loadCard]);

  // Countdown timer
  useEffect(() => {
    if (minTimeLeft <= 0) return;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - cardShownAt.current) / 1000);
      const remaining = Math.max(0, (card?.minTimeSeconds || 0) - elapsed);
      setMinTimeLeft(remaining);
    }, 500);
    return () => clearInterval(interval);
  }, [card, minTimeLeft]);

  // Anti-copy keyboard intercept
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const meta = e.ctrlKey || e.metaKey;
      if (meta && ['c', 'a', 'p', 's', 'u'].includes(e.key.toLowerCase())) {
        const active = document.activeElement;
        const isTextarea = active instanceof HTMLTextAreaElement || active instanceof HTMLInputElement;
        if (!isTextarea) e.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Keyboard MCQ shortcuts
  useEffect(() => {
    if (!card || card.cardType !== 'mcq') return;
    const handler = (e: KeyboardEvent) => {
      const idx = ['a', 'b', 'c', 'd'].indexOf(e.key.toLowerCase());
      if (idx !== -1 && !mcqFeedback) setSelectedOption(idx);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [card, mcqFeedback]);

  const handleSubmit = async () => {
    if (!card || !token || submitting) return;
    const elapsed = Math.floor((Date.now() - cardShownAt.current) / 1000);

    if (elapsed < (card.minTimeSeconds || 0)) {
      setError(`Please take a moment to read before continuing.`);
      return;
    }

    if (card.cardType === 'mcq' && selectedOption === null) {
      setError('Please select an answer.');
      return;
    }

    if (['required', 'open_ended', 'reflection'].includes(card.cardType) && textAnswer.trim().length < 5) {
      setError('Please write a response before continuing.');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const result = await submitCardResponse(token, {
        questionId: card.questionId,
        cardType: card.cardType,
        responseText: textAnswer || undefined,
        selectedOptionIndex: selectedOption !== null ? selectedOption : undefined,
        timeSpentSeconds: elapsed,
      });

      if (card.cardType === 'mcq' && result.isCorrect !== undefined) {
        setMcqFeedback(result.isCorrect ? 'correct' : 'incorrect');
        // Brief pause to show feedback, then advance
        setTimeout(() => {
          if (result.completed) {
            navigate('/training/complete', { replace: true });
          } else if (result.nextCard) {
            transitionToCard(result.nextCard, result.nextCardIndex, result.totalCards);
          }
        }, 1200);
      } else {
        if (result.completed) {
          navigate('/training/complete', { replace: true });
        } else if (result.nextCard) {
          transitionToCard(result.nextCard, result.nextCardIndex, result.totalCards);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const transitionToCard = (nextCard: CardData, nextIndex: number, total: number) => {
    setSlideDir('out');
    setAnimating(true);
    setTimeout(() => {
      setCard(nextCard);
      setCurrentIndex(nextIndex);
      setTotalCards(total);
      setSelectedOption(null);
      setTextAnswer('');
      setMcqFeedback(null);
      setError('');
      cardShownAt.current = Date.now();
      setMinTimeLeft(nextCard.minTimeSeconds);
      setSlideDir('in');
      setTimeout(() => setAnimating(false), 400);
    }, 300);
  };

  const canSubmit =
    !submitting &&
    !mcqFeedback &&
    (card?.cardType === 'week_complete' ||
      (minTimeLeft === 0 && (
        card?.cardType === 'intro' ||
        card?.cardType === 'reading' ||
        (card?.cardType === 'mcq' && selectedOption !== null) ||
        (['required', 'open_ended', 'reflection'].includes(card?.cardType || '') && textAnswer.trim().length >= 5)
      )));

  const progressPct = totalCards > 0 ? Math.round((currentIndex / totalCards) * 100) : 0;
  const weekNum = card?.weekNumber || 1;

  if (loading) {
    return (
      <div className="tg-session tg-session--loading">
        <div className="tg-spinner" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="tg-session tg-session--loading">
        <p className="tg-error">{error || 'Unable to load training.'}</p>
      </div>
    );
  }

  return (
    <div
      className="tg-session"
      onContextMenu={e => e.preventDefault()}
      data-watermark={employeeName}
    >
      {/* Watermark */}
      <div className="tg-watermark" aria-hidden="true">{employeeName}</div>

      {/* Header bar */}
      <div className="tg-header">
        <div className="tg-header__left">
          <span className="tg-header__brand">{brandName}</span>
          <span className="tg-header__week">Week {weekNum} of 8</span>
        </div>
        <div className="tg-header__right">
          <span className="tg-header__name">{employeeName}</span>
          <span className="tg-header__progress">{currentIndex} / {totalCards}</span>
          <button className="tg-exit-btn" onClick={() => setShowExitConfirm(true)}>
            Save &amp; Exit
          </button>
        </div>
      </div>

      {/* Exit confirmation overlay */}
      {showExitConfirm && (
        <div className="tg-overlay" onClick={() => setShowExitConfirm(false)}>
          <div className="tg-overlay__card" onClick={e => e.stopPropagation()}>
            <h2 className="tg-overlay__title">Save &amp; Exit?</h2>
            <p className="tg-overlay__body">
              Your progress is saved. You are on Week {weekNum}, card {currentIndex} of {totalCards}.
              When you return, enter your employee reference to resume from exactly here.
            </p>
            <div className="tg-overlay__actions">
              <button
                className="tg-btn tg-btn--exit"
                onClick={async () => {
                  if (token) await recordExit(token);
                  sessionStorage.removeItem('training_token');
                  sessionStorage.removeItem('training_name');
                  sessionStorage.removeItem('training_brand');
                  navigate('/training', { replace: true });
                }}
              >
                Exit
              </button>
              <button className="tg-btn tg-btn--ghost" onClick={() => setShowExitConfirm(false)}>
                Keep going
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="tg-progress">
        <div className="tg-progress__bar" style={{ width: `${progressPct}%` }} />
      </div>

      {/* Full-screen cards: intro & week_complete */}
      {(card.cardType === 'intro' || card.cardType === 'week_complete') && (
        <div className={`tg-card tg-card--${slideDir} ${animating ? 'tg-card--animating' : ''}`}>
          {card.cardType === 'intro' && (
            <div className="tg-card__inner tg-card__inner--intro">
              <div className="tg-card__week-badge">Week {weekNum}</div>
              <h1 className="tg-card__heading">{card.title}</h1>
              <ul className="tg-card__objectives">
                {card.objectives?.map((obj, i) => <li key={i}>{obj}</li>)}
              </ul>
            </div>
          )}

          {card.cardType === 'week_complete' && (
            <div className={`tg-card__inner tg-card__inner--checkpoint ${card.isBlockBoundary ? 'tg-card__inner--block-end' : ''}`}>
              <div className={`tg-checkpoint__icon ${card.isBlockBoundary ? 'tg-checkpoint__icon--block' : ''}`}>
                {card.isBlockBoundary ? '◆' : '✓'}
              </div>
              <h1 className="tg-card__heading">{card.title}</h1>
              <p className="tg-card__checkpoint-body">{card.body}</p>
              {card.isBlockBoundary && (
                <div className="tg-checkpoint__blocks">
                  <div className="tg-checkpoint__block tg-checkpoint__block--done">
                    <span>Block 1</span>
                    <span className="tg-checkpoint__block-weeks">Weeks 1–4</span>
                    <span className="tg-checkpoint__block-status">Complete</span>
                  </div>
                  <div className="tg-checkpoint__block tg-checkpoint__block--next">
                    <span>Block 2</span>
                    <span className="tg-checkpoint__block-weeks">Weeks 5–8</span>
                    <span className="tg-checkpoint__block-status">Up next</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="tg-card__actions">
            {error && <p className="tg-error">{error}</p>}
            {card.cardType === 'week_complete' ? (
              <div className="tg-card__checkpoint-actions">
                <button className="tg-btn tg-btn--primary" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? '…' : card.isBlockBoundary ? `Continue to Block 2 (Week ${card.nextWeek})` : `Continue to Week ${card.nextWeek}`}
                  <span className="tg-btn__enter">↵</span>
                </button>
                <p className="tg-checkpoint__save-note">
                  Or close the window — your progress is saved and you can resume any time.
                </p>
              </div>
            ) : (
              <button className="tg-btn tg-btn--primary" onClick={handleSubmit} disabled={!canSubmit || submitting}>
                {submitting ? '…' : minTimeLeft > 0 ? `Continue (${minTimeLeft}s)` : 'Continue'}
                <span className="tg-btn__enter">↵</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Split-screen: reading material left, question right */}
      {card.cardType !== 'intro' && card.cardType !== 'week_complete' && (
        <div className="tg-split">
          {/* Left — reading panel */}
          <div className="tg-split__reading">
            <div className="tg-split__reading-label">
              Week {weekNum} — Reading Material
            </div>
            {card.weekReading && <MarkdownRenderer content={card.weekReading} />}
          </div>

          {/* Right — question panel */}
          <div className="tg-split__right">
            <div className={`tg-split__card tg-card--${slideDir} ${animating ? 'tg-card--animating' : ''}`}>

              {card.cardType === 'mcq' && (
                <div className="tg-card__inner tg-card__inner--mcq">
                  <div className="tg-card__type-label">Multiple Choice</div>
                  <h2 className="tg-card__question">{card.question}</h2>
                  <div className="tg-card__options">
                    {card.options?.map((opt, i) => {
                      let cls = 'tg-option';
                      if (selectedOption === i) cls += ' tg-option--selected';
                      if (mcqFeedback === 'correct' && selectedOption === i) cls += ' tg-option--correct';
                      if (mcqFeedback === 'incorrect' && selectedOption === i) cls += ' tg-option--wrong';
                      return (
                        <button key={i} className={cls} onClick={() => !mcqFeedback && setSelectedOption(i)} disabled={!!mcqFeedback}>
                          <span className="tg-option__key">{OPTION_KEYS[i]}</span>
                          <span className="tg-option__text">{opt.text}</span>
                        </button>
                      );
                    })}
                  </div>
                  {mcqFeedback && (
                    <div className={`tg-feedback tg-feedback--${mcqFeedback}`}>
                      {mcqFeedback === 'correct' ? 'Correct' : 'Noted — moving on'}
                    </div>
                  )}
                  <p className="tg-card__hint">Press A / B / C / D to select</p>
                </div>
              )}

              {(card.cardType === 'required' || card.cardType === 'open_ended') && (
                <div className="tg-card__inner tg-card__inner--text">
                  <div className="tg-card__type-label">
                    {card.cardType === 'required' ? 'Knowledge Check' : 'Reflective Question'}
                  </div>
                  <h2 className="tg-card__question">{card.question}</h2>
                  <textarea
                    className="tg-card__textarea"
                    value={textAnswer}
                    onChange={e => setTextAnswer(e.target.value)}
                    placeholder="Write your response here…"
                    rows={5}
                    spellCheck={false}
                  />
                  <p className="tg-card__chars">{textAnswer.trim().length} characters</p>
                </div>
              )}

              {card.cardType === 'reflection' && (
                <div className="tg-card__inner tg-card__inner--reflection">
                  <div className="tg-card__type-label">Reflection</div>
                  <h2 className="tg-card__question">{card.prompt}</h2>
                  <textarea
                    className="tg-card__textarea"
                    value={textAnswer}
                    onChange={e => setTextAnswer(e.target.value)}
                    placeholder="Write your reflection here…"
                    rows={4}
                    spellCheck={false}
                  />
                </div>
              )}

              {!mcqFeedback && (
                <div className="tg-card__actions">
                  {error && <p className="tg-error">{error}</p>}
                  <button
                    className="tg-btn tg-btn--primary"
                    onClick={handleSubmit}
                    disabled={!canSubmit || submitting}
                  >
                    {submitting ? '…' : minTimeLeft > 0 ? `Continue (${minTimeLeft}s)` : 'Continue'}
                    <span className="tg-btn__enter">↵</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
